/**
 * Defense Entry particle effects — burst when defender regains control.
 *
 * 4-layer Sakurai choreography (matches edgeguardEntry pattern but player-colored):
 * 1. Converging motes — pull inward toward defender
 * 2. Radial speed lines — outward burst
 * 3. Dark contrast cavity — near-black center
 * 4. Shockwave ring — expanding player-colored ring
 */

import { configParticle, type ParticleCore } from './types';

/**
 * spawnDefenseEntry — defender control restoration burst.
 * Player-colored instead of cyan. Lighter than DI/burst.
 */
export function spawnDefenseEntry(core: ParticleCore, cx: number, cy: number, playerColor: number): void {
    // === Layer 1: Converging motes — energy gathering inward ===
    const moteCount = 10;
    for (let i = 0; i < moteCount; i++) {
        const angle = (i / moteCount) * Math.PI * 2;
        const dist = 50 + Math.random() * 15;
        const spawnX = cx + Math.cos(angle) * dist;
        const spawnY = cy + Math.sin(angle) * dist;
        const speed = 3 + Math.random() * 2;
        const moteColor = Math.random() < 0.25 ? 0xffffff : playerColor;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: spawnX,
            y: spawnY,
            vx: -Math.cos(angle) * speed, // inward
            vy: -Math.sin(angle) * speed,
            color: moteColor,
            life: 0.2 + Math.random() * 0.08,
            scale: 0.25 + Math.random() * 0.2,
            alpha: 0.8,
            friction: 0.92,
            freezeImmune: true,
        });
    }

    // === Layer 2: Radial speed lines — Sakurai "straight lines vs curves" ===
    const lineCount = 6;
    for (let i = 0; i < lineCount; i++) {
        const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const life = 0.1 + Math.random() * 0.05;
        const lineColor = Math.random() < 0.2 ? 0xffffff : playerColor;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 8,
            outerRadius: 30 + Math.random() * 15,
            expandSpeed: 70 + Math.random() * 30,
            color: lineColor,
            lineWidth: 1.5 + Math.random() * 0.5,
            life,
            maxLife: life,
            freezeImmune: true,
        });
    }

    // === Layer 3: Dark contrast cavity — Sakurai "mix in darker elements" ===
    for (let i = 0; i < 3; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 8,
            y: cy + (Math.random() - 0.5) * 8,
            vx: Math.cos(angle) * (0.3 + Math.random() * 0.5),
            vy: Math.sin(angle) * (0.3 + Math.random() * 0.5),
            color: 0x050510,
            life: 0.12 + Math.random() * 0.08,
            scale: 0.7 + Math.random() * 0.4,
            alpha: 0.45,
            friction: 0.9,
            rotationSpeed: 0,
            addBlend: false,
            hard: false,
            freezeImmune: true,
        });
    }

    // === Layer 4: Expanding shockwave ring — player-colored burst circle ===
    core.spawnShockwave(cx, cy, playerColor, 40, 70, 2.0, 0.15);
}

/**
 * spawnBrakeExplosion — massive radial burst when brake activates (KI Combo Breaker).
 * White + player color, heavy energy release.
 */
export function spawnBrakeExplosion(core: ParticleCore, cx: number, cy: number, playerColor: number): void {
    // White radial burst
    const burstCount = 20;
    for (let i = 0; i < burstCount; i++) {
        const angle = (i / burstCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const speed = 6 + Math.random() * 8;
        const color = Math.random() < 0.4 ? 0xffffff : playerColor;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 6,
            y: cy + (Math.random() - 0.5) * 6,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            color,
            life: 0.2 + Math.random() * 0.15,
            scale: 0.4 + Math.random() * 0.4,
            alpha: 0.9,
            friction: 0.88,
            freezeImmune: true,
        });
    }

    // Dark contrast center
    for (let i = 0; i < 5; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 8,
            y: cy + (Math.random() - 0.5) * 8,
            vx: Math.cos(angle) * 1,
            vy: Math.sin(angle) * 1,
            color: 0x440000,
            life: 0.15 + Math.random() * 0.1,
            scale: 0.8 + Math.random() * 0.5,
            alpha: 0.5,
            friction: 0.9,
            addBlend: false,
            hard: false,
            freezeImmune: true,
        });
    }

    // Speed lines
    const lineCount = 10;
    for (let i = 0; i < lineCount; i++) {
        const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const life = 0.12 + Math.random() * 0.06;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 10,
            outerRadius: 40 + Math.random() * 20,
            expandSpeed: 100 + Math.random() * 50,
            color: Math.random() < 0.3 ? 0xffffff : playerColor,
            lineWidth: 2 + Math.random(),
            life,
            maxLife: life,
            freezeImmune: true,
        });
    }

    // Big shockwave
    core.spawnShockwave(cx, cy, 0xffffff, 60, 120, 3.0, 0.18);
    core.spawnShockwave(cx, cy, playerColor, 80, 100, 2.0, 0.22);
}

/**
 * spawnBrakeWindowPulse — pulsing ring around player during brake window.
 * Intensifies as window closes (progress 1→0).
 */
export function spawnBrakeWindowPulse(core: ParticleCore, cx: number, cy: number, playerColor: number, progress: number): void {
    const alpha = 0.3 + (1 - progress) * 0.5; // brighter as window closes
    const radius = 30 + (1 - progress) * 15;
    core.spawnShockwave(cx, cy, playerColor, radius, 40 + (1 - progress) * 20, 1.5, 0.08);

    // A couple of sparks
    const sparkCount = 2;
    for (let i = 0; i < sparkCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + Math.cos(angle) * radius,
            y: cy + Math.sin(angle) * radius,
            vx: (Math.random() - 0.5) * 2,
            vy: (Math.random() - 0.5) * 2,
            color: playerColor,
            life: 0.06,
            scale: 0.2 + (1 - progress) * 0.15,
            alpha,
            friction: 0.95,
            freezeImmune: true,
        });
    }
}
